/**
 * PATH: packages/ui/src/kanban/audit-revert/readers.ts
 * FILE_ID: UI-KANBAN-AUDIT-REVERT-001-READERS
 * PURPOSE: Type-narrowing readers for the open-ended `[extra: string]: unknown`
 *          shape on AuditLogEntry, plus the shared "missing before-value"
 *          non-reversible plan and the small string truncator. Each reader
 *          returns the typed value or `null` so the case mappers branch cleanly
 *          on missing/malformed data without sprinkling `as` casts.
 * WHY: Lifted verbatim out of the former 540-line auditRevert.ts
 *      (refactor-kanban-decomp3). Pure I→O, no IO, no React.
 */
import type { CardAuditRevertPlan } from './types'

// ---------------------------------------------------------------
// Shared helper for the field-revert cases. Centralises the "audit
// row didn't capture before-value, can't revert" branch so each
// case is one if-guard rather than a duplicated object literal.
// ---------------------------------------------------------------

export function _missingBefore(fieldName: string, friendlyName: string): CardAuditRevertPlan {
  return {
    canRevert: false,
    reason:
      `Audit row missing ${friendlyName} — cannot revert ${fieldName}. ` +
      `(Older audit entries written before qb!2864 won't carry before/after.)`,
  }
}

export function _readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

export function _readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function _readNumberArray(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null
  const out: number[] = []
  for (const v of value) {
    const n = _readNumber(v)
    if (n === null) return null
    out.push(n)
  }
  return out
}

/**
 * Sentinel returned by `_readNullableString` to signal "key not
 * present on the audit row" — distinct from an explicit `null`
 * (key present, value is JSON null, meaning the field WAS empty
 * before the change). The caller surfaces "missing" as
 * non-reversible but "null" as a clear-the-field revert.
 */
export const _MISSING = Symbol('audit_value_missing')

export function _readNullableString(value: unknown): string | null | typeof _MISSING {
  if (value === undefined) return _MISSING
  if (value === null) return null
  if (typeof value === 'string') return value
  // Defensive: a number that looks like a date string can sneak in
  // if a future BE writer forgets to ISO-format. Coerce.
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return _MISSING
}

export function _readBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  // Lenient: some JSON serialisers emit 'true' / 'false' strings.
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

export function _truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return s.slice(0, max - 1) + '…'
}
